import { Search, SentimentDissatisfied } from "@mui/icons-material";
import {
  CircularProgress,
  Grid,
  InputAdornment,
  TextField,
} from "@mui/material";
import { Box } from "@mui/system";
import axios from "axios";
import { useSnackbar } from "notistack";
import React, { useEffect, useState } from "react";
import { config } from "../App";
import Footer from "./Footer";
import Header from "./Header";
import "./Products.css";
import ProductCard from "./ProductCard";
import { Typography } from "@mui/material";
import Cart from "./Cart";

// Definition of Data Structures used
/**
 * @typedef {Object} Product - Data on product available to buy
 *
 * @property {string} name - The name or title of the product


/**
 * @typedef {Object} CartItem -  - Data on product added to cart
 * 
 * @property {string} name - The name or title of the product in cart
 * @property {string} qty - The quantity of product added to cart
 * @property {string} category - The category that the product belongs to
 * @property {number} cost - The price to buy the product
 * @property {number} rating - The aggregate rating of the product (integer out of five)
 * @property {string} image - Contains URL for the product image
 * @property {string} _id - Unique ID for the product
 */

const Products = () => {
  const token = localStorage.getItem("token");
  const { enqueueSnackbar } = useSnackbar();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [debounceTimeout, setDebounceTimeout] = useState(null);
  const [productsArray, setProductsArray] = useState([]);
  const [cartData, setCartData] = useState([]);
  useEffect(() => {
    performAPICall();

    const fetchData = async () => {
      if (token !== null) {
        const items = await fetchCart(token);
        setCartData(items);
      }
    };

    fetchData();
  }, []);

  // TODO: CRIO_TASK_MODULE_PRODUCTS - Fetch products data and store it
  //* @property {string} productId - Unique ID for the product*/

  const Products = () => {
    /**
     * Make API call to get the products list and store it to display the products
     *
     * @returns { Array.<Product> }
     *      Array of objects with complete data on all available products
     *
     * API endpoint - "GET /products"
     *
     * Example for successful response from backend:
     * HTTP 200
     * [
     *      {
     *          "name": "iPhone XR",
     *          "category": "Phones",
     *          "cost": 100,
     *          "rating": 4,
     *          "image": "https://i.imgur.com/lulqWzW.jpg",
     *          "_id": "v4sLtEcMpzabRyfx"
     *      },
     *      {
     *          "name": "Basketball",
     *          "category": "Sports",
     *          "cost": 100,
     *          "rating": 5,
     *          "image": "https://i.imgur.com/lulqWzW.jpg",
     *          "_id": "upLK9JbQ4rMhTwt4"
     *      }
     * ]
     *
     * Example for failed response from backend:
     * HTTP 500
     * {
     *      "success": false,
     *      "message": "Something went wrong. Check the backend console for more details"
     * }
     */
  };
  const performAPICall = async () => {
    setLoading(true);

    const response = await axios
      .get(config.endpoint + "/products")
      .catch(function (error) {
        if (error.response && error.response.statusText) {
          setLoading(false);
          enqueueSnackbar(error.response.statusText, {
            variant: "error",
            autoHideDuration: 1000,
          });
        } else {
          setLoading(false);
          enqueueSnackbar(
            "Something went wrong. Check that the backend is running, reachable and returns valid JSON.",
            { variant: "error", autoHideDuration: 1000 }
          );
        }
      });

    if (response && response.status === 200) {
      setProducts(response.data);
      setProductsArray(response.data);
    } else {
      setProducts([]);
    }
    setLoading(false);
  };

  // TODO: CRIO_TASK_MODULE_PRODUCTS - Implement search logic
  /**
   * Definition for search handler
   * This is the function that is called on adding new search keys
   *
   * @param {string} text
   *    Text user types in the search bar. To filter the displayed products based on this text.
   *
   * @returns { Array.<Product> }
   *      Array of objects with complete data on filtered set of products
   *
   * API endpoint - "GET /products/search?value=<search-query>"
   *
   */
  const performSearch = async (text) => {
    // Make a GET request to the search endpoint
    const response = await axios
      .get(`${config.endpoint}/products/search?value=${text}`)
      .catch(function (error) {
        if (error.response && error.response.statusText) {
          enqueueSnackbar(error.response.statusText, {
            variant: "error",
            autoHideDuration: 1000,
          });
        } else {
          enqueueSnackbar(
            "Something went wrong. Check that the backend is running, reachable and returns valid JSON.",
            { variant: "error", autoHideDuration: 1000 }
          );
        }
      });

    if (response && response.status === 200) {
      setProducts(response.data);
    } else {
      setProducts([]);
    }
  };

  // TODO: CRIO_TASK_MODULE_PRODUCTS - Optimise API calls with debounce search implementation
  /**
   * Definition for debounce handler
   * With debounce, this is the function to be called whenever the user types text in the searchbar field
   *
   * @param {{ target: { value: string } }} event
   *    JS event object emitted from the search input field
   *
   * @param {NodeJS.Timeout} debounceTimeout
   *    Timer id set for the previous debounce call
   *
   */
  const debounceSearch = (event, debounceTimeout) => {
    // Adjust the debounce delay as needed
    const value = event.target.value;

    if (debounceTimeout) {
      clearTimeout(debounceTimeout);
    }

    const timeout = setTimeout(async () => {
      await performSearch(value);
    }, 500);

    setDebounceTimeout(timeout);
  };

  /**
   * Perform the API call to fetch the user's cart and return the response
   *
   * @param {string} token - Authentication token returned on login
   *
   * @returns { Array.<{ productId: string, qty: number }> | null }
   *    The response JSON object
   *
   * Example for successful response from backend:
   * HTTP 200
   * [
   *      {
   *          "productId": "KCRwjF7lN97HnEaY",
   *          "qty": 3
   *      },
   *      {
   *          "productId": "BW0jAAeDJmlZCF8i",
   *          "qty": 1
   *      }
   * ]
   *
   * Example for failed response from backend:
   * HTTP 401
   * {
   *      "success": false,
   *      "message": "Protected route, Oauth2 Bearer token not found"
   * }
   */
  const fetchCart = async (token) => {
    if (!token) return;    
    console.log(token);
    try {
      // TODO: CRIO_TASK_MODULE_CART - Pass Bearer token inside "Authorization" header to get data from "GET /cart" API and return the response data

      var response = await axios.get(config.endpoint + "/cart", {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });      
      return response.data;
    } catch (e) {
      console.log("error");
      console.log(e);
      if (e.response && e.response.status === 400) {
        enqueueSnackbar(e.response.data.message, { variant: "error" });
      } else {
        enqueueSnackbar(
          "Could not fetch cart details. Check that the backend is running, reachable and returns valid JSON.",
          {
            variant: "error",
          }
        );
      }
      return null;
    }
  };

  // TODO: CRIO_TASK_MODULE_CART - Return if a product already exists in the cart
  /**
   * Return if a product already is present in the cart
   *
   * @param { Array.<{ productId: String, quantity: Number }> } items
   *    Array of objects with productId and quantity of products in cart
   * @param { String } productId
   *    Id of a product to be checked
   *
   * @returns { Boolean }
   *    Whether a product of given "productId" exists in the "items" array
   *
   */
  const isItemInCart = (items, productId) => {
    var item = items.find((element) => element.productId === productId);
    if (item) return true;
    else return false;
  };

  /**
   * Perform the API call to add or update items in the user's cart and update local cart data to display the latest cart
   *
   * @param {string} token
   *    Authentication token returned on login
   * @param { Array.<{ productId: String, quantity: Number }> } items
   *    Array of objects with productId and quantity of products in cart
   * @param { Array.<Product> } products
   *    Array of objects with complete data on all available products
   * @param {string} productId
   *    ID of the product that is to be added or updated in cart
   * @param {number} qty
   *    How many of the product should be in the cart
   * @param {boolean} options
   *    If this function was triggered from the product card's "Add to Cart" button
   *
   * Example for successful response from backend:
   * HTTP 200 - Updated list of cart items
   * [
   *      {
   *          "productId": "KCRwjF7lN97HnEaY",
   *          "qty": 3
   *      },
   *      {
   *          "productId": "BW0jAAeDJmlZCF8i",
   *          "qty": 1
   *      }
   * ]
   *
   * Example for failed response from backend:
   * HTTP 404 - On invalid productId
   * {
   *      "success": false,
   *      "message": "Product doesn't exist"
   * }
   */
  const addToCart = async (
    token,
    items,
    products,
    productId,
    qty,
    options = { preventDuplicate: false }
  ) => {

    if (token === null) {
      enqueueSnackbar("Login to add an item to the Cart", {
        variant: "warning",
        autoHideDuration: 1000,
      });
    }

    if (options.preventDuplicate && isItemInCart(items, productId)) {

      enqueueSnackbar(
        "Item already in cart. Use the cart sidebar to update quantity or remove item.",
        {
          variant: "warning",
          autoHideDuration: 1000,
        }
      );
    } else {
      try {
        const response = await axios.post(
          `${config.endpoint}/cart`,
          {
            productId: productId,
            qty: qty,
          },
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        if (response.status === 200) {
          const updatedCart = response.data;
          setCartData(updatedCart);
        }
      } catch (error) {
        enqueueSnackbar(error, {
          variant: "error",
          autoHideDuration: 1000,
        });
      }
    }
  };

  return (
    <div>
      <Header>
        {
          /* TODO: CRIO_TASK_MODULE_PRODUCTS - Display search bar in the header for Products page */
          <TextField
            className="search-desktop"
            size="small"
            fullWidth
            placeholder="Search for items/categories"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search color="primary" />
                </InputAdornment>
              ),
            }}
            name="searchHeader"
            onChange={(e) => debounceSearch(e, debounceTimeout)}
          />
        }
      </Header>

      <TextField
        className="search-mobile"
        size="small"
        fullWidth
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <Search color="primary" />
            </InputAdornment>
          ),
        }}
        placeholder="Search for items/categories"
        name="search"
        onChange={(event) => debounceSearch(event, debounceTimeout)}
      />
      <Grid container>
        <Grid item xs={12} md={token !== null ? 9 : 12}>
          <Grid item className="product-grid">
            <Box className="hero">
              <p className="hero-heading">
                India’s <span className="hero-highlight">FASTEST DELIVERY</span>{" "}
                to your door step
              </p>
            </Box>
          </Grid>

          <Grid container spacing={2} sx={{ "padding" : 2 } }>
            {loading ? (
              <Grid
                item
                justifyContent="center" // Center horizontally
                alignItems="center"
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center", // Center both horizontally and vertically
                  width: "100%",
                  padding: "16px",
                  flexDirection: "column",
                }}
              >
                <CircularProgress />
                <Typography variant="body1">Loading Products</Typography>
              </Grid>
            ) : products.length === 0 ? (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center", // Center both horizontally and vertically
                  width: "100%",
                  padding: "16px",
                  flexDirection: "column",
                }}
              >
                <SentimentDissatisfied fontSize="large" color="error" />
                <Typography variant="h6">No products found</Typography>
              </Box>
            ) : (
              products.map((product) => (
                <Grid
                  item
                  xs={6}
                  md={3}
                  sx={{ padding: "8px" }}
                  key={product._id}
                >
                  <ProductCard
                    product={product}
                    handleAddToCart={() =>    
                      addToCart(token, cartData, products, product._id, 1, { preventDuplicate: true })
                    }
                  />
                </Grid>
              ))
            )}
          </Grid>
        </Grid>
        {token !== null ? (
          <Grid item xs={12} md={3} sx={{ backgroundColor: "#E9F5E1" }}>
            <Cart
              products={productsArray}
              items={cartData}
              isReadOnly = {false}
              handleQuantity={(qty, id) =>
                addToCart(token, cartData, products, id, qty, { preventDuplicate: false })
              }
            />
          </Grid>
        ) : null}
      </Grid>

      <Footer />
    </div>
  );
};

export default Products;
